import { useState } from 'react';

import { useSearchParams } from 'react-router';

import { ColumnNames, toColumnName } from '../types/ExtendEnums';
import type { VentetiderForbehandling } from '../types/Helsenorge.VelgBehandlingssted.Libraries.EntitiesEntities';
import type { HNVelgBehandlingsstedFrontend } from '../types/Resources';

import { createFilterConfig, useFilter, useFilterDrawer } from '@helsenorge/designsystem-react/components/Filter';
import { simpleConfig } from '@helsenorge/designsystem-react/components/Table';

import PageHeader from '@helsenorge/core-framework/page-header';
import PageLoader from '@helsenorge/core-framework/page-loader';
import { Table, Column, ExpandableRow } from '@helsenorge/data-table/components/table';
import { getSidetittelId } from '@helsenorge/framework-utils/hn-page';

import AntallCell from './antall-cell';
import BehandlingsstedExpanded from './behandlingssted-expanded';
import { type VentetiderForBehandlingFilters, getTabellData, getInitialFilters, getTableCaption } from './helper';
import VentetidCell from './ventetid-cell';
import BehandlingsstedCell from '../shared/behandlingssted-cell';
import Filter from '../shared/filter/filter';
import { getFilterResources } from '../utils/filters';

import './styles.scss';

interface Props {
  resources: HNVelgBehandlingsstedFrontend;
  loading: boolean;
  error?: string;
  ventetiderForBehandlingByBid?: VentetiderForbehandling | null;
}

const VentetiderForBehandlingComponent: React.FC<Props> = ({ resources, loading, error, ventetiderForBehandlingByBid }: Props) => {
  const [searchparams] = useSearchParams();
  const bid = searchparams.get('bid') ?? '';
  const harAntall = ventetiderForBehandlingByBid?.harAntall;

  const [sortColumnKey, setSortColumnKey] = useState(ColumnNames.Behandlingssted);
  const [sortDirIsAsc, setSortDirIsAsc] = useState(true);
  const behandlingssteder = ventetiderForBehandlingByBid?.behandlingssteder ?? [];
  const filterData = getInitialFilters(resources, behandlingssteder);
  const { filterOptions, getLabel } = createFilterConfig<VentetiderForBehandlingFilters>({
    helseregion: {
      options: filterData[0]?.values.map(({ value, displayName }) => ({ value, label: displayName ?? value })),
      getLabel: x => x.label,
    },
    fylke: {
      options: filterData[1]?.values.map(({ value, displayName }) => ({ value, label: displayName ?? value })),
      getLabel: x => x.label,
    },
    tilbud: {
      options: filterData[2]?.values.map(({ value, displayName }) => ({ value, label: displayName ?? value })),
      getLabel: x => x.label,
    },
    fritekst: { getLabel: value => String(value) },
  });
  const filter = useFilter(filterOptions);
  const drawer = useFilterDrawer<keyof VentetiderForBehandlingFilters>();
  const tabellData = getTabellData(resources, behandlingssteder, filter.filters);

  const onSort = (sortColumnKey: string, sortAscending: boolean): void => {
    setSortColumnKey(toColumnName(sortColumnKey));
    setSortDirIsAsc(sortAscending);
  };

  const filterResources = getFilterResources(resources);
  const caption = getTableCaption(
    resources,
    ventetiderForBehandlingByBid && ventetiderForBehandlingByBid.navn ? ventetiderForBehandlingByBid.navn : ''
  );

  const labels = {
    helseregion: resources.filterHelseregion,
    fylke: resources.filterFylke,
    tilbud: resources.filterTilbud,
  };

  return (
    <PageLoader resources={resources} loading={loading} error={error} labelId={getSidetittelId()}>
      <PageHeader
        subtitle={resources.ventetiderForBehandlingTittelPrefix}
        title={ventetiderForBehandlingByBid?.navn ?? ''}
        description={ventetiderForBehandlingByBid && !!ventetiderForBehandlingByBid.kommentar ? ventetiderForBehandlingByBid.kommentar : ''}
        hjelpetekstId="ventetiderforbehandling"
      />
      <Filter
        filter={filter}
        drawer={drawer}
        getLabel={getLabel}
        resources={filterResources}
        resultCount={tabellData.length}
        resultLabel={resources.ventetiderForBehandlingFilterUnit}
        title={resources.filterTitle}
        categories={[
          { key: 'helseregion', title: labels.helseregion },
          { key: 'fylke', title: labels.fylke },
          { key: 'tilbud', title: labels.tilbud },
        ]}
        filterData={filterData}
      />
      <Table
        id="ventetider-for-behandling-table"
        data={tabellData}
        tableClassNames={`vbs-ventetider-for-behandling__behandlingssted-tabell ${
          harAntall ? 'vbs-ventetider-for-behandling__behandlingssted-tabell--four-columns' : ''
        }`}
        initialSortColumnKey={sortColumnKey}
        initialSortDirIsAsc={sortDirIsAsc}
        sortColumnKey={sortColumnKey}
        sortDirIsAsc={sortDirIsAsc}
        onSortColumn={onSort}
        showMoreButtonClasses="vbs-main__hent-flere"
        showMorePageSize={20}
        showMoreButtonText={resources.hentFlere}
        emptyContentText={resources.tabellIngenTreff}
        useNumericSort
        caption={caption}
        noAnchorLinkWrapper
        breakpointConfig={simpleConfig}
      >
        <Column
          name={ColumnNames.Behandlingssted}
          title={resources.ventetiderForBehandlingTabellBehandlingssted}
          sortable
          cell={<BehandlingsstedCell />}
        />
        <Column
          name={ColumnNames.Ventetid}
          title={resources.ventetiderForBehandlingTabellVentetid}
          sortable
          cell={<VentetidCell resources={resources} />}
        />
        {harAntall && (
          <Column
            name={ColumnNames.Antall}
            title={resources.ventetiderForBehandlingTabellAntall}
            sortable
            cell={<AntallCell resources={resources} />}
          />
        )}

        <ExpandableRow open tableId="ventetider-for-behandling-table">
          <BehandlingsstedExpanded resources={resources} bid={bid} />
        </ExpandableRow>
      </Table>
    </PageLoader>
  );
};

export default VentetiderForBehandlingComponent;
